import { bufferToHexString } from '@iotize/common/byte-converter';
import { TapAuth } from '@iotize/tap/auth';
import { TapStreamWriter, TlvBundleConverter } from '@iotize/tap/client/impl';
import {
  BaseVariableConfig,
  BundleConfig,
  VariableConfig,
} from '@iotize/tap/config/schema/v1';
import { ADMIN_USER } from '@iotize/tap/configurator';
import { getConverterFromVariableType } from '@iotize/tap/data';
import { VariableType } from '@iotize/tap/service/impl/variable';
import { VirtualTarget } from './api/virtual-target';
import { ConfigExplorer } from './config-explorer';
import { debug } from './debug';
import { TapServerError } from './tap-server-error';
import { VirtualVariableState } from './virtual-variable-state';

const TAG = 'VirtualTarget';
export class VirtualTargetDataCom {
  public isConnected = false;

  bundleConverter: TlvBundleConverter<Record<string, Uint8Array>>;

  private target?: VirtualTarget;

  constructor(public configExplorer: ConfigExplorer) {
    this.bundleConverter = new TlvBundleConverter(configExplorer.variables);
    this.configExplorer.configChange.subscribe(() => {
      this.bundleConverter = new TlvBundleConverter(configExplorer.variables);
    });
  }

  setTarget(target: VirtualTarget) {
    if (this.target) {
      this.target.stop(); // TODO catch error ?
    }
    this.target = target;
  }

  private get variableState(): VirtualVariableState | undefined {
    return this.target?.getVariables();
  }

  connect() {
    this._checkTarget();
    debug(TAG, `Connecting target`);
    this.target!.connect();
    this.isConnected = true;
  }

  disconnect() {
    this._checkTarget();
    debug(TAG, `Disconnecting target`);
    this.target!.disconnect();
    this.isConnected = false;
  }

  getValue(id: number): Uint8Array {
    this._checkTargetConnectRequiredForVariable(id);
    const value = this.variableState!.getValue(id);
    const variableConfig = this.configExplorer.getVariableById(id);
    return encodeVariableValue(value, variableConfig);
  }

  checkConnected() {
    if (!this.isConnected) {
      throw TapServerError.notAvailable();
    }
  }

  async stop() {
    if (this.target) {
      debug('Stopping target...');
      await this.target.stop();
    } else {
      debug('Nothing to stop. No virtual target.');
    }
  }

  private _checkTargetConnectRequiredForProfile(session: TapAuth.SessionState) {
    const profileVariables = this._getProfileVariables(session);
    const hasTargetVariable = profileVariables?.find(
      (variable) => variable.domain === VariableType.Domain.TARGET
    );
    if (hasTargetVariable) {
      this.checkConnected();
    }
  }

  private _checkTargetConnectRequiredForVariable(id: number) {
    if (
      this.configExplorer.getVariableById(id).domain ===
      VariableType.Domain.TARGET
    ) {
      this.checkConnected();
    }
  }

  private _checkTargetConnectRequiredForBundle(id: number) {
    const bundleConfig = this.getBundleConfig(id);
    const hasTargetVariable = bundleConfig.variables?.find(
      (variable) => variable.domain === VariableType.Domain.TARGET
    );
    if (hasTargetVariable) {
      this.checkConnected();
    }
  }

  private _checkTarget() {
    if (!this.target) {
      throw TapServerError.notAvailable(); // TODO proper error
    }
  }

  setValue(id: number, encodedValue: Uint8Array) {
    this._checkTargetConnectRequiredForVariable(id);
    debug(
      TAG,
      `Set value to variable id "${id}": 0x${bufferToHexString(encodedValue)}`
    );
    if (!encodedValue || encodedValue.length === 0) {
      throw new Error(`Variable value cannot be empty`);
    }
    const variableConfig = this.configExplorer.getVariableById(id);
    const value = decodeVariableValue(encodedValue, variableConfig);
    return this.variableState!.setValue(id, value);
  }

  getProfileValues(session: TapAuth.SessionState) {
    this._checkTargetConnectRequiredForProfile(session);
    const variables = this._getProfileVariables(session);
    // debug(`Profile ${session.name} (id: ${session.groupId}) values: ${JSON.stringify(variables)}`);
    return this._encodeVariables(variables);
  }

  private _getProfileVariables(session: TapAuth.SessionState) {
    return this.configExplorer.variablesWithMeta
      .filter((info) => {
        if (!info.bundle || !info.bundle.acls) {
          return session.groupId === ADMIN_USER.id;
        }
        return (
          Object.keys(info.bundle.acls)
            .map((key) => ({ key, value: info.bundle!.acls![key] }))
            .filter((acl) => {
              return acl.key === session.name && acl.value.read === true;
            }).length > 0
        );
      })
      .map((info) => info.variable);
  }

  getBundleConfig(bundleId: number): BundleConfig {
    return this.configExplorer.getBundleById(bundleId);
  }

  getBundleValues(bundleId: number): Uint8Array {
    this._checkTargetConnectRequiredForBundle(bundleId);
    // this.generateValues();
    const variables = this.getBundleConfig(bundleId).variables || [];
    // debug(`Bunde ${bundleId} values: ${JSON.stringify(variables)}`);
    return this._encodeVariables(variables, bundleId);
  }

  private _encodeVariables(
    variables: BaseVariableConfig[],
    bundleId: number = 0
  ) {
    const result: Record<number, Uint8Array> = {};
    for (const variable of variables) {
      result[variable.id] = this.getValue(variable.id);
    }
    this.bundleConverter.bundleId = bundleId;
    debug(`Generated variables values: ${JSON.stringify(result)}`);
    return this.bundleConverter.encode(result);
  }
}

function decodeVariableValue(
  value: Uint8Array,
  variable: VariableConfig
): number {
  const converter = getConverter(variable);
  return converter.decode(value);
}

function encodeVariableValue(
  value: number | number[],
  variable: VariableConfig
): Uint8Array {
  const result = new TapStreamWriter();
  const converter = getConverter(variable);
  result.writeBytes(converter.encode(value));
  return result.toBytes;
}

function getConverter(variable: VariableConfig) {
  const length = variable.length || 1;
  const variableType =
    VariableType.Data[variable.dataType as keyof typeof VariableType.Data];
  if (variableType === undefined) {
    throw new Error(`Invalid variable type for ${JSON.stringify(variable)}`);
  }
  const converter = getConverterFromVariableType(variableType, length);

  // console.log('Generated raw', value);
  if (!converter) {
    throw new Error(
      `No converter for variable ${variable.meta?.name || 'UNAMED'} (id: ${
        variable.id
      }). Type: ${variable.dataType}`
    );
  }
  return converter;
}
